import React, { useState } from 'react';

/* ─────────────────────────────────────────────
   STYLES
───────────────────────────────────────────── */
const styles = `
  /* Variables and font imports are handled by global.css */

  /* ── Section ── */
  .faq-section {
    padding: 100px 60px;
    border-top: 1px solid var(--border-soft);
    background: var(--card-bg);
    position: relative;
    overflow: hidden;
  }
  .faq-section::before {
    content: '';
    position: absolute; inset: 0; pointer-events: none;
    background: radial-gradient(ellipse 55% 50% at 50% 0%, rgba(245,200,0,0.04) 0%, transparent 65%);
  }
  .faq-inner {
    max-width: 1100px;
    margin: 0 auto;
    position: relative;
    z-index: 1;
  }

  /* ── Header ── */
  .faq-header {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 48px;
    align-items: flex-end;
    margin-bottom: 72px;
  }
  .faq-eyebrow {
    font-size: 10px; letter-spacing: 4px; text-transform: uppercase;
    color: var(--accent); margin-bottom: 14px;
    display: flex; align-items: center; gap: 10px;
  }
  .faq-eyebrow::before {
    content: ''; width: 24px; height: 1px;
    background: var(--accent); display: inline-block;
  }
  .faq-heading {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(40px, 5.5vw, 72px);
    line-height: .95; color: var(--white);
  }
  .faq-heading em {
    font-family: 'Instrument Serif', serif;
    font-style: italic; color: var(--gold);
  }
  .faq-sub {
    font-size: 14px; line-height: 1.85;
    color: var(--muted-light); max-width: 340px; margin-top: 16px;
  }
  .faq-meta { display: flex; flex-direction: column; gap: 20px; align-items: flex-start; }
  .faq-count {
    display: inline-flex; align-items: center; gap: 10px;
    font-size: 11px; letter-spacing: 2px; text-transform: uppercase;
    color: var(--muted-light);
    padding: 8px 16px; border: 1px solid var(--border-soft); border-radius: 100px;
  }
  .faq-count strong {
    font-family: 'Bebas Neue', sans-serif; font-size: 20px;
    color: var(--accent); line-height: 1;
  }
  .faq-meta-sub {
    font-size: 12px; color: var(--muted); line-height: 1.7; max-width: 280px;
  }
  .faq-ask-link {
    display: inline-flex; align-items: center; gap: 8px;
    font-size: 11px; letter-spacing: 2px; text-transform: uppercase;
    color: var(--accent); cursor: pointer; background: none; border: none;
    font-family: 'DM Sans', sans-serif; transition: gap .25s; padding: 0;
  }
  .faq-ask-link:hover { gap: 14px; }
  .faq-ask-link svg { width: 13px; height: 13px; }

  /* ── 2-col accordion grid ── */
  .faq-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    align-items: start;
  }

  /* ── Single item ── */
  .faq-item {
    border: 1px solid var(--border-soft);
    border-radius: 6px;
    background: rgba(255,255,255,0.02);
    overflow: hidden;
    transition: border-color .3s;
  }
  .faq-item.open   { border-color: rgba(245,200,0,0.22); }
  .faq-item:hover  { border-color: rgba(245,200,0,0.14); }

  /* Trigger */
  .faq-trigger {
    width: 100%; text-align: left; background: none; border: none; cursor: pointer;
    padding: 20px 22px;
    display: flex; align-items: flex-start; justify-content: space-between; gap: 16px;
    color: var(--white); font-family: 'DM Sans', sans-serif; font-weight: 300;
  }
  .faq-trigger-inner {
    display: flex; align-items: flex-start; gap: 14px; flex: 1; min-width: 0;
  }
  .faq-num {
    font-family: 'Bebas Neue', sans-serif; font-size: 13px;
    color: var(--accent); opacity: .45; flex-shrink: 0;
    line-height: 1.5; transition: opacity .3s;
  }
  .faq-item.open .faq-num { opacity: 1; }

  .faq-question {
    font-size: 14px; line-height: 1.5; color: var(--white);
    font-weight: 400; flex: 1; min-width: 0; transition: color .25s;
    text-align: left;
  }
  .faq-item.open .faq-question { color: var(--accent); }

  /* Icon */
  .faq-icon {
    width: 28px; height: 28px; border-radius: 50%; flex-shrink: 0;
    border: 1px solid var(--border-soft);
    display: flex; align-items: center; justify-content: center;
    background: rgba(245,200,0,0.04);
    transition: background .3s, border-color .3s, transform .35s;
  }
  .faq-item.open .faq-icon {
    background: var(--accent); border-color: var(--accent);
    transform: rotate(45deg);
  }
  .faq-icon svg { width: 12px; height: 12px; transition: stroke .3s; }
  .faq-icon svg line { stroke: var(--muted-light); }
  .faq-item.open .faq-icon svg line { stroke: #0a0900; }

  /* Animated body */
  .faq-body {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows .38s ease;
  }
  .faq-item.open .faq-body { grid-template-rows: 1fr; }
  .faq-body-inner { overflow: hidden; }

  .faq-answer {
    font-size: 13px; line-height: 1.85; color: var(--muted-light);
    padding: 0 22px 20px 58px;   /* 58px = 22px left + 14px gap + 22px num width */
    border-top: 1px solid var(--border-soft);
    padding-top: 14px;
    margin-top: 0;
  }

  /* ── Bottom strip ── */
  .faq-bottom {
    display: flex; align-items: center; justify-content: space-between;
    gap: 20px; flex-wrap: wrap;
    margin-top: 40px; padding-top: 32px;
    border-top: 1px solid var(--border-soft);
  }
  .faq-bottom-text { font-size: 13px; color: var(--muted-light); line-height: 1.7; }
  .faq-bottom-text strong { color: var(--white); font-weight: 500; }
  .faq-bottom-btn {
    display: inline-flex; align-items: center; gap: 8px;
    background: var(--accent); color: #0a0900; border: none;
    padding: 12px 28px; font-size: 11px; letter-spacing: 2px; text-transform: uppercase;
    font-family: 'DM Sans', sans-serif; font-weight: 500; border-radius: 2px;
    cursor: pointer; transition: background .25s, transform .2s;
    text-decoration: none;
  }
  .faq-bottom-btn:hover { background: var(--gold); transform: translateY(-1px); }
  .faq-bottom-btn svg { width: 13px; height: 13px; }

  /* ── Responsive ── */
  @media (max-width: 960px) {
    .faq-header { grid-template-columns: 1fr; gap: 28px; margin-bottom: 48px; }
    .faq-grid   { grid-template-columns: 1fr; }
    .faq-section{ padding: 72px 28px; }
  }
  @media (max-width: 600px) {
    .faq-section  { padding: 64px 16px; }
    .faq-bottom   { flex-direction: column; align-items: flex-start; }
    .faq-question { font-size: 13px; }
    .faq-answer   { padding-left: 22px; }
  }
`;

/* ─────────────────────────────────────────────
   DATA
───────────────────────────────────────────── */
const FAQ_ITEMS = [
  {
    question: 'How quickly do you respond to contact requests?',
    answer: 'We aim to reply within 24 hours on business days. If your request is urgent, please mention it in the message.',
  },
  {
    question: 'What is your pricing model?',
    answer: 'We offer flexible pricing depending on project scope, timeline, and recurring engagement. After discovery, we provide a transparent proposal.',
  },
  {
    question: 'Can you work with existing teams and products?',
    answer: 'Yes. We integrate with your tools and teams, and can collaborate as a full-service partner or augment your internal capabilities.',
  },
  {
    question: 'Do you provide post-launch support?',
    answer: 'Absolutely. We offer maintenance, optimization, and growth support packages after launch to keep performance improving over time.',
  },
  {
    question: 'How do you handle revisions?',
    answer: 'We include a set number of revisions in scope and provide a clear change request process so updates are efficient and aligned with your goals.',
  },
  {
    question: 'What if my project timeline changes?',
    answer: 'We stay flexible. We\'ll rephase work based on your priorities and update deliverables with transparent timeline and cost adjustments.',
  },
  {
    question: 'Can you help with SEO and content as well?',
    answer: 'Yes. We provide SEO optimization, content strategy, and production to maximize organic visibility and conversion performance.',
  },
  {
    question: 'Do you build scalable infrastructure?',
    answer: 'Yes. We focus on clean architecture, performance, and security so your platform can grow with user and data demand.',
  },
  {
    question: 'How do you report progress?',
    answer: 'We use regular status updates, shared task boards, and monthly reports. You always know what\'s done, what\'s next, and where we are.',
  },
  {
    question: 'What are the first steps to start a project?',
    answer: 'Start with a discovery call, share your goals and current assets, and we\'ll send a custom proposal with a clear roadmap.',
  },
];

/* ─────────────────────────────────────────────
   SUB-COMPONENTS
───────────────────────────────────────────── */

/* Plus icon — rotates to × when open via CSS on parent */
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const ArrowRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

/* Animated accordion item using CSS grid-template-rows trick */
const FaqItem = ({ item, index, isOpen, onToggle }) => {
  const num = String(index + 1).padStart(2, '0');

  return (
    <div className={`faq-item${isOpen ? ' open' : ''}`}>
      <button
        type="button"
        className="faq-trigger"
        aria-expanded={isOpen}
        onClick={onToggle}
      >
        <div className="faq-trigger-inner">
          <span className="faq-num">{num}</span>
          <span className="faq-question">{item.question}</span>
        </div>
        <div className="faq-icon">
          <PlusIcon />
        </div>
      </button>

      <div className="faq-body" role="region">
        <div className="faq-body-inner">
          <p className="faq-answer">{item.answer}</p>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
const LandingFaq = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (index) =>
    setOpenIndex((prev) => (prev === index ? null : index));

  return (
    <>
      <style>{styles}</style>

      <section className="faq-section" id="faq">
        <div className="faq-inner">

          {/* ── Header ── */}
          <div className="faq-header">
            <div>
              <p className="faq-eyebrow">FAQ</p>
              <h2 className="faq-heading">
                Frequently <em>Asked</em><br />Questions
              </h2>
              <p className="faq-sub">
                Quick answers to common questions about working with our
                digital growth team.
              </p>
            </div>

            <div className="faq-meta">
              <div className="faq-count">
                <strong>{FAQ_ITEMS.length}</strong>
                questions answered
              </div>
              <p className="faq-meta-sub">
                Still have questions? Reach out directly and we'll get back
                to you within 24 hours.
              </p>
              <a href="#contact" className="faq-ask-link">
                Ask us directly <ArrowRight />
              </a>
            </div>
          </div>

          {/* ── Accordion grid ── */}
          <div className="faq-grid">
            {FAQ_ITEMS.map((item, index) => (
              <FaqItem
                key={item.question}
                item={item}
                index={index}
                isOpen={openIndex === index}
                onToggle={() => toggle(index)}
              />
            ))}
          </div>

          {/* ── Bottom strip ── */}
          <div className="faq-bottom">
            <p className="faq-bottom-text">
              Still not sure?{' '}
              <strong>Book a free 30-min discovery call</strong> — no
              pressure, no commitment.
            </p>
            <a href="#contact" className="faq-bottom-btn">
              Book a Discovery Call <ArrowRight />
            </a>
          </div>

        </div>
      </section>
    </>
  );
};

export default LandingFaq;